import { ScrollTextIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuthedFetcher } from '@/hooks/fetch';

type EventType = 'success' | 'warning' | 'error' | 'info';

type TimelineEvent = {
    id: string;
    time: string;
    title: string;
    detail: string;
    type: EventType;
};

function relativeTime(ts: number): string {
    const diff = Math.floor((Date.now() / 1000) - ts);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

function classifyEntry(action: string): EventType {
    const a = action.toLowerCase();
    if (a.includes('error') || a.includes('ban') || a.includes('kick') || a.includes('fail')) return 'error';
    if (a.includes('warn') || a.includes('restart') || a.includes('stop')) return 'warning';
    if (a.includes('start') || a.includes('login') || a.includes('create') || a.includes('success')) return 'success';
    return 'info';
}

export default function ActivityTimelineCard() {
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const fetcher = useAuthedFetcher();

    const load = async () => {
        try {
            const data = await fetcher<{ rows: { id: number; action: string; detail: string; timestamp: number; username?: string }[] }>('/fivem/audit');
            if (data?.rows?.length) {
                const mapped: TimelineEvent[] = data.rows.slice(0, 20).map((r) => ({
                    id: String(r.id),
                    time: relativeTime(r.timestamp),
                    title: r.action,
                    detail: r.detail ?? (r.username ? `by ${r.username}` : ''),
                    type: classifyEntry(r.action),
                }));
                setEvents(mapped);
            }
        } catch {
            // bridge offline — leave list empty
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 30_000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getDotColor = (type: EventType) => {
        switch (type) {
            case 'success': return 'bg-scGreen shadow-[0_0_8px_var(--sc-success)]';
            case 'warning': return 'bg-warning shadow-[0_0_8px_var(--sc-warn)]';
            case 'error': return 'bg-destructive shadow-[0_0_8px_var(--sc-danger)]';
            default: return 'bg-scCyan shadow-[0_0_8px_var(--sc-cyan)]';
        }
    };

    return (
        <div className="py-4 px-5 rounded-xl border bg-card/60 border-white/5 backdrop-blur-xl shadow-md flex flex-col h-[22rem] overflow-hidden group hover:border-white/10 transition-all duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-white/[0.04] mb-3">
                <h3 className="tracking-tight text-sm font-semibold flex items-center gap-1.5 text-slate-200">
                    <ScrollTextIcon size={14} className="text-scCyan" />
                    Activity Timeline
                </h3>
                <span className="text-[10px] font-mono text-muted-foreground/80 uppercase tracking-widest">Audit Log</span>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-white/5 hover:scrollbar-thumb-white/10">
                {loading ? (
                    <div className="flex items-center justify-center h-full text-xs text-muted-foreground">Loading…</div>
                ) : events.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-xs text-muted-foreground">No activity recorded.</div>
                ) : (
                    <div className="relative border-l border-white/[0.05] ml-2 pl-4 space-y-5 py-1">
                        {events.map((event) => (
                            <div key={event.id} className="relative flex flex-col gap-1 text-xs">
                                <div className={`absolute left-[-21px] top-1 size-2 rounded-full ring-4 ring-[#070910] ${getDotColor(event.type)}`} />
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-semibold text-slate-100">{event.title}</span>
                                    <span className="text-[9px] font-mono text-muted-foreground shrink-0">{event.time}</span>
                                </div>
                                {event.detail && (
                                    <span className="text-[11px] text-muted-foreground leading-normal">{event.detail}</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
